import {
    VorankuendigungCoordinator,
    VorankuendigungData,
    VorankuendigungManualField,
    VorankuendigungParty,
    VorankuendigungPerson,
} from '../types';

/**
 * Datenmodell der Vorankündigung — reine Funktionen, ohne DOM und ohne Supabase.
 *
 * Die Vorankündigung führte Personen früher als mehrzeiligen Freitext ("Name",
 * "Tel: …", darunter ein Block "Vertreter:"). Für Punkt 2, 4 und 5 müssen
 * mehrere Personen sauber getrennt erfassbar sein, deshalb liegen sie jetzt als
 * Listen vor. `normalizeVorankuendigung` holt Altstände — auch die
 * Archiv-Schnappschüsse — beim Lesen in dieses Format, sodass jede Ansicht
 * (Editor, Vorschau, PDF) nur noch die Listen kennt.
 */

let idCounter = 0;

const createId = (): string => {
    const uuid = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto?.randomUUID;
    return uuid ? uuid.call((globalThis as { crypto?: Crypto }).crypto) : `vk-${++idCounter}`;
};

const PHONE_LABEL = /^(tel\.?|telefon|fon|mobil|handy)\s*[:.]?\s*/i;
const MAIL_LABEL = /^(e-?\s?mail|mail)\s*[:.]?\s*/i;

export interface ContactBlock {
    role?: string;
    lines: string[];
}

/**
 * Zerlegt einen Freitextblock in Personen-Abschnitte.
 *
 * Eine Zeile, die auf ":" endet und kein Kontaktfeld ist ("Vertreter:",
 * "2. SiGeKo:"), beginnt einen neuen Abschnitt und liefert dessen Rolle.
 */
export const parseContactBlocks = (raw: string): ContactBlock[] => {
    const blocks: ContactBlock[] = [];
    let current: ContactBlock = { lines: [] };

    (raw || '').split('\n').forEach(line => {
        const text = line.trim();
        if (!text) return;
        const isContact = PHONE_LABEL.test(text) || MAIL_LABEL.test(text);
        if (!isContact && text.endsWith(':')) {
            if (current.lines.length || current.role) blocks.push(current);
            current = { role: text.slice(0, -1).trim() || undefined, lines: [] };
            return;
        }
        current.lines.push(text);
    });

    if (current.lines.length || current.role) blocks.push(current);
    return blocks;
};

const takeLabelled = (lines: string[], label: RegExp): string | undefined => {
    const index = lines.findIndex(l => label.test(l));
    if (index === -1) return undefined;
    const [line] = lines.splice(index, 1);
    return line.replace(label, '').trim() || undefined;
};

/** Aus den Zeilen eines Abschnitts Name, Telefon und E-Mail herauslösen. */
export const splitContactLines = (lines: string[]): { rest: string[]; phone?: string; email?: string } => {
    const rest = [...lines];
    const phone = takeLabelled(rest, PHONE_LABEL);
    const email = takeLabelled(rest, MAIL_LABEL);
    return { rest, phone, email };
};

export const emptyPerson = (role?: string): VorankuendigungPerson => ({ id: createId(), role, name: '' });

export const emptyParty = (): VorankuendigungParty => ({ id: createId(), name: '', address: '' });

export const emptyCoordinator = (role?: string): VorankuendigungCoordinator => ({
    id: createId(),
    role,
    company: '',
    name: '',
});

/** Freitext → Personenliste (Punkt 2). */
export const parsePersonList = (raw: string): VorankuendigungPerson[] =>
    parseContactBlocks(raw).map(block => {
        const { rest, phone, email } = splitContactLines(block.lines);
        return { id: createId(), role: block.role, name: rest.join(', '), phone, email };
    });

/** Freitext → Liste verantwortlicher Dritter (Punkt 4): erste Zeile Name, Rest Anschrift. */
export const parsePartyList = (raw: string): VorankuendigungParty[] => {
    const blocks = parseContactBlocks(raw);
    if (!blocks.length) return [];
    return blocks.map(block => {
        const { rest, phone, email } = splitContactLines(block.lines);
        return {
            id: createId(),
            role: block.role,
            name: rest[0] || '',
            address: rest.slice(1).join('\n'),
            phone,
            email,
        };
    });
};

/**
 * Firmenanschrift + Personen-Freitext → Koordinatorenliste (Punkt 5 a/b).
 *
 * Die Anschrift gilt für alle Einträge des Blocks: der Vertreter saß im alten
 * Format in derselben Firma, nur ohne eigene Adresszeile.
 */
export const parseCoordinatorList = (company: string, person: string): VorankuendigungCoordinator[] => {
    const blocks = parseContactBlocks(person);
    const address = (company || '').trim();

    if (!blocks.length) {
        return address ? [{ ...emptyCoordinator(), company }] : [];
    }

    return blocks.map(block => {
        const { rest, phone, email } = splitContactLines(block.lines);
        return {
            id: createId(),
            role: block.role,
            company,
            name: rest.join(', '),
            phone,
            email,
        };
    });
};

const hasContent = (entry: { name?: string; company?: string; address?: string; phone?: string; email?: string }): boolean =>
    Boolean(
        (entry.name || '').trim() ||
        (entry.company || '').trim() ||
        (entry.address || '').trim() ||
        (entry.phone || '').trim() ||
        (entry.email || '').trim()
    );

/**
 * Altstand in das Listenformat heben. Idempotent: liegen die Listen bereits vor,
 * bleibt der Datensatz unverändert.
 */
export const normalizeVorankuendigung = (data: VorankuendigungData): VorankuendigungData => {
    const next: VorankuendigungData = { ...data };

    if (!next.clientPersons) next.clientPersons = [];

    if (!next.responsibleThirdParties) {
        next.responsibleThirdParties = parsePartyList(next.responsibleThirdParty || '');
    }

    if (!next.planningCoordinators) {
        const list = parseCoordinatorList(next.coordinatorPlanningCompany || '', next.coordinatorPlanningPerson || '');
        if (next.planningCoordinatorId && list[0]) list[0].coordinatorId = next.planningCoordinatorId;
        next.planningCoordinators = list;
    }

    if (!next.executionCoordinators) {
        const list = parseCoordinatorList(next.coordinatorExecutionCompany || '', next.coordinatorExecutionPerson || '');
        if (next.executionCoordinatorId && list[0]) list[0].coordinatorId = next.executionCoordinatorId;
        next.executionCoordinators = list;
    }

    if (!Array.isArray(next.employersList)) next.employersList = [];
    if (!next.manualFields) next.manualFields = [];

    return next;
};

/** Nur Einträge mit Inhalt — leere Zeilen des Editors gehören nicht in das Dokument. */
export const filledPersons = (list: VorankuendigungPerson[] | undefined): VorankuendigungPerson[] =>
    (list || []).filter(hasContent);

export const filledParties = (list: VorankuendigungParty[] | undefined): VorankuendigungParty[] =>
    (list || []).filter(hasContent);

export const filledCoordinators = (list: VorankuendigungCoordinator[] | undefined): VorankuendigungCoordinator[] =>
    (list || []).filter(hasContent);

/** Anzeigename mit vorangestellter Rolle: "Vertreter: Erika Muster". */
export const describeRole = (role: string | undefined, name: string): string => {
    const r = (role || '').trim();
    const n = (name || '').trim();
    if (r && n) return `${r}: ${n}`;
    return r || n;
};

/** Kontaktzeilen einer Person für Vorschau und PDF. */
export const contactLines = (entry: { phone?: string; email?: string }): string[] => {
    const lines: string[] = [];
    if ((entry.phone || '').trim()) lines.push(`Tel.: ${entry.phone!.trim()}`);
    if ((entry.email || '').trim()) lines.push(`E-Mail: ${entry.email!.trim()}`);
    return lines;
};

// ── Punkte 7–10: Handeingabe gegen den Abgleich sperren ─────────────────────

export const isManualField = (data: VorankuendigungData, field: VorankuendigungManualField): boolean =>
    (data.manualFields || []).includes(field);

export const markManualField = (
    data: VorankuendigungData,
    field: VorankuendigungManualField,
): VorankuendigungManualField[] =>
    isManualField(data, field) ? (data.manualFields || []) : [...(data.manualFields || []), field];

export const clearManualField = (
    data: VorankuendigungData,
    field: VorankuendigungManualField,
): VorankuendigungManualField[] => (data.manualFields || []).filter(f => f !== field);
